import React, { useState, useEffect } from 'react';
import { useUtilityRates } from '../../hooks/useUtilityRates';
import { LoadingSpinner } from '../general/LoadingSpinner';
import { FaSearch, FaEye } from 'react-icons/fa';

/**
 * RateListPanel - Embeddable rate list with search/filter (no routing/sidebar)
 * @param {Function} onSelectRate - Callback when a rate is selected: (rateSummary) => void
 * @param {string} selectedRateId - Currently selected rate ID (for highlighting)
 * @param {boolean} compact - Show compact version (for embedding)
 */
const RateListPanel = ({ onSelectRate, selectedRateId = null, compact = false }) => {
  const { listRates, listUtilities, hasAuth } = useUtilityRates();
  
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedUtility, setSelectedUtility] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = compact ? 20 : 50;

  const [availableUtilities, setAvailableUtilities] = useState([]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Fetch rates
  useEffect(() => {
    fetchRates();
  }, [currentPage, searchTerm, selectedSector, selectedUtility]);

  // Fetch utility list
  useEffect(() => {
    fetchUtilities();
  }, [selectedSector, searchTerm]);

  const fetchUtilities = async () => {
    if (!hasAuth) return;
    
    try {
      const utilities = await listUtilities({
        search: searchTerm,
        sector: selectedSector,
      });
      setAvailableUtilities(utilities);
    } catch (err) {
      console.error('Error fetching utilities list:', err);
      setAvailableUtilities([]);
    }
  };

  const fetchRates = async () => {
    if (!hasAuth) {
      setError('Authentication required. Please log in to access utility rates.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await listRates({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm,
        sector: selectedSector,
        utility_name: selectedUtility,
      });

      setRates(result.rates);
      setTotalPages(result.total_pages);
      setTotalCount(result.total_count);
    } catch (err) {
      console.error('Error fetching utility rates:', err);
      setError('Failed to load utility rates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedSector('');
    setSelectedUtility('');
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (!hasAuth) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <p className="text-yellow-800 font-medium">Authentication required to access utility rates database.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Search and Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <form onSubmit={handleSearch} className="space-y-3">
          {/* Search Bar */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by utility, rate name, or sector..."
                  className="w-full px-3 py-2 pl-9 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400 text-xs" />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sector</label>
              <select
                value={selectedSector}
                onChange={(e) => {
                  setSelectedSector(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Sectors</option>
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
                <option value="Industrial">Industrial</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Utility</label>
              <select
                value={selectedUtility}
                onChange={(e) => {
                  setSelectedUtility(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Utilities</option>
                {availableUtilities.map((utility) => (
                  <option key={utility} value={utility}>{utility}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={handleReset}
                className="w-full px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Results Section */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner message="Loading rates..." />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 font-medium text-sm">{error}</p>
          </div>
        ) : rates.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-600 font-ui">No utility rates found matching your criteria.</p>
            <button
              onClick={handleReset}
              className="mt-3 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-3">
              <p className="text-xs text-gray-600 font-ui">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount} rates
              </p>
            </div>

            {/* Results Table */}
            <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-auto h-full">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Utility Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Rate Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Sector
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Effective Date
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rates.map((rate) => (
                      <tr
                        key={rate.id}
                        className={`transition-colors cursor-pointer ${
                          selectedRateId === rate.id 
                            ? 'bg-blue-50 hover:bg-blue-100' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => onSelectRate && onSelectRate(rate)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {rate.utility_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {rate.rate_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {rate.sector}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {formatDate(rate.effective_date)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectRate && onSelectRate(rate);
                            }}
                            className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs"
                          >
                            <FaEye />
                            <span>View</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-3 flex justify-center items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                
                <div className="flex gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RateListPanel;
